import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import classnames from 'classnames';
import { FaCog, FaFileExcel } from 'react-icons/fa';

import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import RelationName from '@/shared/components/RelationName';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import enumCache from '@/shared/services/EnumCacheService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import { showWhenPermission } from '@/shared/components/security';
import { injectStateService } from '@/shared/services/StateService';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import DataCacheService from '@/shared/services/DataCacheService';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import userStorage from '@/shared/services/userStorage';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import ScrollArrow from '@/shared/components/ScrollArrow';
import RelationRentabilityFilters, { defaultFilters } from './RelationRentabilityFilters';
import messages from '../reportingMessages';
import NavigateToState from '../../../shared/components/NavigateToState';
import { functionalities, privileges } from '@/shared/services/AuthService';

const SessionStorageKey = 'RelationRentabilityReportParams';
const columnSelectionStorageKey = 'RelationRentabilityReportHiddenColumns';

// Init prop= visible on init
const columnDefinitions = [
  { id: 'invoicingType', message: messages.InvoicingType, init: true },
  { id: 'registeredHours', message: messages.RegisteredHours, init: true },
  { id: 'billedHours', message: messages.BilledHours, init: true },
  { id: 'addedHours', message: messages.AddedHours, init: false },
  { id: 'removedHours', message: messages.RemovedHours, init: false },
  { id: 'inForfaitHours', message: messages.InForfait, init: true },
  { id: 'registeredCosts', message: messages.RegisteredCosts, init: true },
  { id: 'billedCosts', message: messages.BilledCosts, init: true },
  { id: 'fixedAmounts', message: messages.FixedAmountOrAdvance, init: true },
  { id: 'freeLines', message: messages.FreeLines, init: true },
  { id: 'turnover', message: messages.Turnover, init: true },
  { id: 'internalCost', message: messages.InternalCost, init: false },
  { id: 'profit', message: messages.Profit, init: false },
  { id: 'turnoverMinRegistered', message: messages.Rentability, init: true },
  { id: 'openRegisteredHours', message: messages.OpenRegisteredHours, init: true },
  { id: 'openRegisteredCosts', message: messages.OpenRegisteredCosts, init: false },
  { id: 'neverInvoiceRegisteredHours', message: messages.NeverInvoiceRegisteredHours, init: false },
  { id: 'neverInvoiceRegisteredCosts', message: messages.NeverInvoiceRegisteredCosts, init: false },
  { id: 'writtenOffAmount', message: messages.WrittenOffAmount, init: false },
];

class RelationRentabilityContainer extends Component {
  state = {
    reportFilters: {},
    rows: null,
    showHours: false,
  };

  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }
    const columnsFromStorage = userStorage.getItem(columnSelectionStorageKey);
    const initColumns = typeof columnsFromStorage === 'undefined' || columnsFromStorage === null;

    const hiddenColumns = columnsFromStorage || [];
    const hideableColumns = columnDefinitions.map(({ id, message, init = true }) => ({
      id,
      label: intl.formatMessage(message),
      selected: !initColumns ? hiddenColumns.indexOf(id) === -1 : !!init,
    }));
    const columnStatus = hideableColumns.reduce((o, col) => {
      // eslint-disable-next-line no-param-reassign
      o[col.id] = !!col.selected;
      return o;
    }, {});

    this.state = {
      ...this.state,
      reportFilters: {
        ...defaultFilters,
        ...sessionParams,
        ...searchParams,
      },
      hideableColumns,
      columnStatus,
      showColumnSelectionModal: false,
      showHours: !!sessionStorage.getItem('RelationRentabilityReportShowHours'),
    };
  }

  componentDidMount() {
    const { intl } = this.props;

    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
    this.retrieve();

    enumCache.get('invoicingtype').then(invoicingtypes => {
      this.invoicingtypes = invoicingtypes.reduce(
        (o, item) => ({
          ...o,
          [item.key]: intl.formatMessage(listItemMsg('invoicingtype', item.key, item.value)),
        }),
        {},
      );
    });
  }

  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getParams() {
    const { reportFilters } = this.state;
    const {
      relationId,
      dateFrom: from,
      dateUntil: until,
      relationTypes,
      invoicingType,
      ownCompanies,
      responsible,
      assistant,
      assignmentTemplateId,
      invoiceSchedule,
      withLabels,
      withoutLabels,
      teamCodes,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      includeInactive,
      filterBehaviours,
      relationGroups,
      sectors,
    } = reportFilters;

    return {
      relationId,
      from,
      until,
      relationTypes,
      invoicingType,
      ownCompanies,
      responsible,
      assistant,
      assignmentTemplateId,
      invoiceSchedule,
      withLabels,
      withoutLabels,
      teamCodes,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      includeInactive,
      filterBehaviours,
      relationGroups,
      sectors,
    };
  }

  retrieve() {
    const { reportFilters } = this.state;

    const params = this.getParams();

    const sessionParams = JSON.stringify(reportFilters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();
    this.setState({ rows: null });
    return api.base
      .post(`${config.appUrls.reporting}/api/relationrentability`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          rows: data,
        }));
        return data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  }

  createExcelReport = () => {
    const { intl } = this.props;

    const reportParams = this.getParams();
    const languageCode = intl.locale;

    const params = {
      language: languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      report: 1,
      parameters: JSON.stringify(reportParams),
    };

    return api.base
      .post(`${config.appUrls.reporting}/api/export`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  filtersChanged(filters) {
    this.setState(
      state => ({
        ...state,
        reportFilters: {
          ...state.reportFilters,
          ...filters,
        },
      }),
      () => this.retrieve(),
    );
  }

  handleColumnSelection = items => {
    let hiddenColumns = items.filter(x => !x.selected).map(x => x.id);
    // Clear selection if all are hidden or none are hidden
    if (hiddenColumns.length === items.length || hiddenColumns.length === 0) hiddenColumns = [];
    userStorage.setItem(columnSelectionStorageKey, hiddenColumns);

    const { hideableColumns } = this.state;

    const newHideableColumns = hideableColumns.map(x => ({
      ...x,
      selected: hiddenColumns.indexOf(x.id) === -1,
    }));
    const columnStatus = newHideableColumns.reduce((o, col) => {
      // eslint-disable-next-line no-param-reassign
      o[col.id] = !!col.selected;
      return o;
    }, {});

    this.setState({
      hideableColumns: newHideableColumns,
      columnStatus,
      showColumnSelectionModal: false,
    });
  };

  renderHoursColumn = value => (
    <td className={classnames('text-right hours', { 'text-muted': !value })}>
      {(value || 0).toTime()}
    </td>
  );
  renderHoursColumnWithNavigation = (value, stateName, navigationProps = {}) => (
    <td className={classnames('text-right hours', { 'text-muted': !value })}>
      <NavigateToState stateName={stateName} useUrl paramsToJson {...navigationProps}>
        {(value || 0).toTime()}
      </NavigateToState>
    </td>
  );
  renderAmountColumn = value => (
    <td className={classnames('text-right amount', { 'text-muted': !value })}>
      <FormattedCurrency value={value || 0} />
    </td>
  );
  renderAmountColumnWithNavigation = (value, stateName, navigationProps = {}) => (
    <td className={classnames('text-right amount', { 'text-muted': !value })}>
      <NavigateToState stateName={stateName} useUrl paramsToJson {...navigationProps}>
        <FormattedCurrency value={value || 0} />
      </NavigateToState>
    </td>
  );
  renderHoursOrAmount = (isHours, hoursValue, amountValue) =>
    isHours ? this.renderHoursColumn(hoursValue) : this.renderAmountColumn(amountValue);
  renderHoursOrAmountWithNavigation = (
    isHours,
    hoursValue,
    amountValue,
    stateName,
    navigationProps = {},
  ) =>
    isHours
      ? this.renderHoursColumnWithNavigation(hoursValue, stateName, navigationProps)
      : this.renderAmountColumnWithNavigation(amountValue, stateName, navigationProps);

  calculateTotal = (rows, property) =>
    rows.reduce((sum, { [property]: value = 0 }) => sum + value, 0);

  showHoursChanged = value => {
    sessionStorage.setItem('RelationRentabilityReportShowHours', value);
    this.setState({ showHours: value });
  };

  renderRow = (row, showHours, showAssignments) => {
    const { intl } = this.props;
    const { columnStatus, reportFilters } = this.state;

    const now = moment.utc();
    const relation = {
      id: row.relationId,
      type: row.relationType,
      name: row.relationDisplayName,
      alias: row.relationAlias,
      isActive:
        !row.relationEndDate ||
        (row.relationEndDate && moment.utc(row.relationEndDate).isAfter(now)),
      relationHandlingLevel: row.relationHandlingLevel,
      relationHandlingLevelClarification: row.relationHandlingLevelClarification,
    };
    const dateFrom = reportFilters.dateFrom ? moment(reportFilters.dateFrom).toDate() : null;
    const dateUntil = reportFilters.dateUntil ? moment(reportFilters.dateUntil).toDate() : null;

    return (
      <tr
        className={classnames({ 'row-highlighted': row.selected })}
        key={`${row.relationId}-${row.assignmentId}`}
        onClick={() =>
          this.setState(state => ({
            ...state,
            rows: state.rows.map(x => ({
              ...x,
              selected: x.relationId === row.relationId,
            })),
          }))
        }
      >
        <td className="text-left">
          <RelationName relation={relation} />
        </td>
        {showAssignments ? <td>{mapInvoiceStreamName(row, intl, '')}</td> : null}
        {columnStatus.invoicingType && (
          <td title={row.invoicingScheduleName}>
            {!row.invoicingType && row.invoicingType !== 0 ? (
              <FormattedMessage {...messages.None} />
            ) : (
              this.invoicingtypes[row.invoicingType]
            )}
          </td>
        )}
        {columnStatus.registeredHours &&
          this.renderHoursOrAmountWithNavigation(
            showHours,
            row.registeredHours,
            row.registeredHoursAmount,
            'registrations.overview',
            {
              stateParams: {
                relationId: row.relationId,
                relation,
                finalInvoiceStreamId: row.invoiceStreamId,
                period: 'all',
                invoiceDateFrom: dateFrom,
                invoiceDateUntil: dateUntil,
                filters: {
                  type: { items: [0] },
                },
              },
            },
          )}

        {columnStatus.billedHours &&
          this.renderHoursOrAmount(showHours, row.billedHours, row.billedHoursAmount)}
        {columnStatus.inForfaitHours &&
          this.renderHoursOrAmountWithNavigation(
            showHours,
            row.inForfaitHours,
            row.inForfaitHoursAmount,
            'registrations.overview',
            {
              stateParams: {
                relationId: row.relationId,
                relation,
                finalInvoiceStreamId: row.invoiceStreamId,
                period: 'all',
                invoiceDateFrom: dateFrom,
                invoiceDateUntil: dateUntil,
                filters: {
                  type: { items: [0] },
                  forfaitStatus: { items: [2] },
                },
              },
            },
          )}
        {columnStatus.addedHours &&
          this.renderHoursOrAmount(showHours, row.addedHours, row.addedHoursAmount)}
        {columnStatus.removedHours &&
          this.renderHoursOrAmount(showHours, row.removedHours, row.removedHoursAmount)}

        {columnStatus.registeredCosts &&
          this.renderAmountColumnWithNavigation(row.registeredCosts, 'registrations.overview', {
            stateParams: {
              relationId: row.relationId,
              relation,
              finalInvoiceStreamId: row.invoiceStreamId,
              period: 'all',
              invoiceDateFrom: dateFrom,
              invoiceDateUntil: dateUntil,
              filters: {
                type: { items: [1, 2] },
              },
            },
          })}
        {columnStatus.billedCosts && this.renderAmountColumn(row.billedCosts)}
        {columnStatus.fixedAmounts && this.renderAmountColumn(row.fixedAmounts)}
        {columnStatus.freeLines && this.renderAmountColumn(row.freeLines)}
        {columnStatus.writtenOffAmount &&
          this.renderAmountColumnWithNavigation(row.writtenOffAmount, 'invoicing.overview', {
            stateParams: {
              relationId: row.relationId,
              relation,
              invoiceStreamId: row.invoiceStreamId,
              dateFrom,
              dateUntil,
              invoiceDateFrom: null,
              invoiceDateUntil: null,
              showWrittenOffInternally: 1,
            },
            functionality: 'InvoiceFinal',
            permission: 'Read',
          })}
        {columnStatus.turnover &&
          this.renderAmountColumnWithNavigation(row.turnover, 'invoicing.overview', {
            stateParams: {
              relationId: row.relationId,
              relation,
              invoiceStreamId: row.invoiceStreamId,
              dateFrom,
              dateUntil,
              invoiceDateFrom: null,
              invoiceDateUntil: null,
            },
            functionality: 'InvoiceFinal',
            permission: 'Read',
          })}
        {columnStatus.internalCost && this.renderAmountColumn(row.internalCost)}
        {columnStatus.profit && this.renderAmountColumn(row.profit)}
        {columnStatus.turnoverMinRegistered && this.renderAmountColumn(row.turnoverMinRegistered)}
        {columnStatus.openRegisteredHours &&
          this.renderHoursOrAmountWithNavigation(
            showHours,
            row.openRegisteredHours,
            row.openRegisteredHoursAmount,
            'registrations.overview',
            {
              stateParams: {
                relationId: row.relationId,
                relation,
                finalInvoiceStreamId: row.invoiceStreamId,
                period: 'all',
                filters: {
                  invoiceStatus: { items: [0, 1] },
                  type: { items: [0] },
                },
              },
            },
          )}

        {columnStatus.openRegisteredCosts &&
          this.renderAmountColumnWithNavigation(row.openRegisteredCosts, 'registrations.overview', {
            stateParams: {
              relationId: row.relationId,
              relation,
              finalInvoiceStreamId: row.invoiceStreamId,
              period: 'all',
              filters: {
                invoiceStatus: { items: [0, 1] },
                type: { items: [1, 2] },
              },
            },
          })}
        {columnStatus.neverInvoiceRegisteredHours &&
          this.renderHoursOrAmountWithNavigation(
            showHours,
            row.neverInvoiceRegisteredHours,
            row.neverInvoiceRegisteredHoursAmount,
            'registrations.overview',
            {
              stateParams: {
                relationId: row.relationId,
                relation,
                finalInvoiceStreamId: row.invoiceStreamId,
                period: 'all',
                filters: {
                  invoiceStatus: { items: [-1] },
                  type: { items: [0] },
                },
              },
            },
          )}

        {columnStatus.neverInvoiceRegisteredCosts &&
          this.renderAmountColumnWithNavigation(
            row.neverInvoiceRegisteredCosts,
            'registrations.overview',
            {
              stateParams: {
                relationId: row.relationId,
                relation,
                finalInvoiceStreamId: row.invoiceStreamId,
                period: 'all',
                filters: {
                  invoiceStatus: { items: [-1] },
                  type: { items: [1, 2] },
                },
              },
            },
          )}
      </tr>
    );
  };

  render() {
    const { intl } = this.props;
    const {
      reportFilters,
      rows,
      showHours,
      showColumnSelectionModal,
      columnStatus,
      hideableColumns,
    } = this.state;
    const showAssignments = this.assignmentsEnabled && !!rows && rows.some(x => !!x.assignmentId);

    return (
      <div className="relation-rentability">
        <SimplePanel
          article="5434879956242"
          title={intl.formatMessage(messages.RelationRentability)}
        >
          <div className="info-block container-fluid">
            <RelationRentabilityFilters
              filters={reportFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              showHours={showHours}
              onShowHoursChanged={this.showHoursChanged}
            />
          </div>
        </SimplePanel>
        {showColumnSelectionModal ? (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        ) : null}
        {rows ? (
          <>
            <div className="row btn-row">
              <div className="col-md-6 pull-right content-right">
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.setState({ showColumnSelectionModal: true })}
                >
                  <span className="margin5R">
                    <FaCog className="react-icon" />
                  </span>
                  <FormattedMessage {...messages.ColumnConfig} />
                </Button>
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.createExcelReport()}
                  showProgress
                >
                  <span className="margin5R">
                    <FaFileExcel className="file-icon" />
                  </span>
                  {intl.formatMessage(messages.ExportToExcel)}
                </Button>
              </div>
            </div>
            <table className="table table-report relation-rentability margin50B">
              <thead>
                <tr>
                  <th className="sticky-header">
                    <FormattedMessage {...messages.Relation} />
                  </th>
                  {showAssignments ? (
                    <th className="sticky-header">
                      <FormattedMessage {...messages.Assignment} />
                    </th>
                  ) : null}
                  {columnStatus.invoicingType && (
                    <th className="sticky-header">
                      <FormattedMessage {...messages.InvoicingType} />
                    </th>
                  )}
                  {columnStatus.registeredHours && (
                    <th className="hours sticky-header text-right">
                      <FormattedMessage {...messages.RegisteredHours} />
                    </th>
                  )}
                  {columnStatus.billedHours && (
                    <th className="hours sticky-header text-right">
                      <FormattedMessage {...messages.BilledHours} />
                    </th>
                  )}
                  {columnStatus.inForfaitHours && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.InForfait} />
                    </th>
                  )}
                  {columnStatus.addedHours && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.AddedHours} />
                    </th>
                  )}
                  {columnStatus.removedHours && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.RemovedHours} />
                    </th>
                  )}
                  {columnStatus.registeredCosts && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.RegisteredCosts} />
                    </th>
                  )}
                  {columnStatus.billedCosts && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.BilledCosts} />
                    </th>
                  )}
                  {columnStatus.fixedAmounts && (
                    <th className="large-amount sticky-header text-right">
                      <FormattedMessage {...messages.FixedAmountOrAdvance} />
                    </th>
                  )}
                  {columnStatus.freeLines && (
                    <th className="large-amount sticky-header text-right">
                      <FormattedMessage {...messages.FreeLines} />
                    </th>
                  )}
                  {columnStatus.writtenOffAmount && (
                    <th className="large-amount sticky-header text-right">
                      <FormattedMessage {...messages.WrittenOffAmount} />
                    </th>
                  )}
                  {columnStatus.turnover && (
                    <th className="large-amount sticky-header text-right">
                      <FormattedMessage {...messages.Turnover} />
                    </th>
                  )}
                  {columnStatus.internalCost && (
                    <th className="large-amount sticky-header text-right">
                      <FormattedMessage {...messages.InternalCost} />
                    </th>
                  )}
                  {columnStatus.profit && (
                    <th
                      className="large-amount sticky-header text-right"
                      title={intl.formatMessage(messages.TurnoverMinInternalCost)}
                    >
                      <FormattedMessage {...messages.Profit} />
                    </th>
                  )}
                  {columnStatus.turnoverMinRegistered && (
                    <th
                      className="large-amount sticky-header text-right"
                      title={intl.formatMessage(messages.TurnoverMinRegistered)}
                    >
                      <FormattedMessage {...messages.Rentability} />
                    </th>
                  )}
                  {columnStatus.openRegisteredHours && (
                    <th className="hours sticky-header text-right">
                      <FormattedMessage {...messages.OpenRegisteredHours} />
                    </th>
                  )}
                  {columnStatus.openRegisteredCosts && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.OpenRegisteredCosts} />
                    </th>
                  )}
                  {columnStatus.neverInvoiceRegisteredHours && (
                    <th className="hours sticky-header text-right">
                      <FormattedMessage {...messages.NeverInvoiceRegisteredHours} />
                    </th>
                  )}
                  {columnStatus.neverInvoiceRegisteredCosts && (
                    <th className="amount sticky-header text-right">
                      <FormattedMessage {...messages.NeverInvoiceRegisteredCosts} />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>{rows.map(item => this.renderRow(item, showHours, showAssignments))}</tbody>
              <tfoot>
                <tr>
                  <th colSpan={(showAssignments ? 2 : 1) + (columnStatus.invoicingType ? 1 : 0)}>
                    <FormattedMessage {...messages.Totals} />
                  </th>
                  {columnStatus.registeredHours &&
                    showHours &&
                    this.renderHoursColumn(this.calculateTotal(rows, 'registeredHours'))}
                  {columnStatus.registeredHours &&
                    !showHours &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'registeredHoursAmount'))}
                  {columnStatus.billedHours &&
                    showHours &&
                    this.renderHoursColumn(this.calculateTotal(rows, 'billedHours'))}
                  {columnStatus.billedHours &&
                    !showHours &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'billedHoursAmount'))}
                  {columnStatus.inForfaitHours &&
                    showHours &&
                    this.renderHoursColumn(this.calculateTotal(rows, 'inForfaitHours'))}
                  {columnStatus.inForfaitHours &&
                    !showHours &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'inForfaitHoursAmount'))}
                  {columnStatus.addedHours &&
                    showHours &&
                    this.renderHoursColumn(this.calculateTotal(rows, 'addedHours'))}
                  {columnStatus.addedHours &&
                    !showHours &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'addedhoursAmount'))}
                  {columnStatus.removedHours &&
                    showHours &&
                    this.renderHoursColumn(this.calculateTotal(rows, 'removedHours'))}
                  {columnStatus.removedHours &&
                    !showHours &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'removedHoursAmount'))}

                  {columnStatus.registeredCosts &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'registeredCosts'))}
                  {columnStatus.billedCosts &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'billedCosts'))}
                  {columnStatus.fixedAmounts &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'fixedAmounts'))}
                  {columnStatus.freeLines &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'freeLines'))}
                  {columnStatus.writtenOffAmount &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'writtenOffAmount'))}
                  {columnStatus.turnover &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'turnover'))}
                  {columnStatus.internalCost &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'internalCost'))}
                  {columnStatus.profit &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'profit'))}
                  {columnStatus.turnoverMinRegistered &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'turnoverMinRegistered'))}

                  {columnStatus.openRegisteredHours &&
                    showHours &&
                    this.renderHoursColumn(this.calculateTotal(rows, 'openRegisteredHours'))}
                  {columnStatus.openRegisteredHours &&
                    !showHours &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'openRegisteredHoursAmount'))}

                  {columnStatus.openRegisteredCosts &&
                    this.renderAmountColumn(this.calculateTotal(rows, 'openRegisteredCosts'))}

                  {columnStatus.neverInvoiceRegisteredHours &&
                    showHours &&
                    this.renderHoursColumn(
                      this.calculateTotal(rows, 'neverInvoiceRegisteredHours'),
                    )}
                  {columnStatus.neverInvoiceRegisteredHours &&
                    !showHours &&
                    this.renderAmountColumn(
                      this.calculateTotal(rows, 'neverInvoiceRegisteredHoursAmount'),
                    )}

                  {columnStatus.neverInvoiceRegisteredCosts &&
                    this.renderAmountColumn(
                      this.calculateTotal(rows, 'neverInvoiceRegisteredCosts'),
                    )}
                </tr>
              </tfoot>
            </table>
            <ScrollArrow />
          </>
        ) : (
          <LoadingAnimation />
        )}
      </div>
    );
  }
}

RelationRentabilityContainer.propTypes = {
  intl: intlShape.isRequired,
  searchParams: PropTypes.shape({ relationId: PropTypes.string }),
};

RelationRentabilityContainer.defaultProps = {
  searchParams: {},
};

export default showWhenPermission(
  functionalities.RentabilityReport,
  privileges.Read,
)(injectStateService(injectIntl(RelationRentabilityContainer)));
